import { Eye } from 'lucide-react'
import { useState } from 'react'
import { trpc } from '../lib/trpc'
import type { Sitzung } from '../lib/queries'

/**
 * **Der Balken, der sagt: du bist gerade nicht du** (24.08.2026,
 * [E-214](../../../../docs/ENTSCHEIDUNGEN.md)).
 *
 * Läuft eine Sicht („Anmelden als"), zeigt die Anwendung **alles** mit den Augen der
 * Zielperson – Navigation, Aufgaben, „Mein Tag". Genau das ist der Zweck, und genau das ist
 * die Gefahr: es gibt keinen anderen Hinweis darauf, dass man in fremden Listen liest. Wer
 * das nach fünf Minuten vergisst, sucht anschließend seine eigenen Aufgaben.
 *
 * ## Warum unten und nicht oben
 *
 * Oben sitzt bereits der Hinweis auf eine neue Fassung (`AppShell`), und der ist vorübergehend.
 * Zwei feste Balken übereinander verdeckten sich. Unten ist er außerdem **dauerhaft im
 * Bild**, ohne die Ansicht nach unten zu schieben – das Raster der Anwendung bleibt, wie es
 * ist.
 *
 * ## Warum das Beenden neu lädt
 *
 * Weil der Bestand im Client liegt (Leitplanke 2): Aufgaben, Pools, Feed, Auswertungen und
 * jeder optimistisch geflickte Eintrag gehören der Person, mit deren Augen sie geholt wurden.
 * Einzelne Abfragen zu verwerfen hieße, sich für jede zu erinnern – ein Neuladen ist die eine
 * Handlung, die nichts übrig lässt. Bei einem Wechsel, den ein Admin ein paarmal am Tag
 * vornimmt, ist die Sekunde gut angelegt.
 */
export function SichtBalken({ person }: { person: Sitzung }) {
  const [laeuft, setLaeuft] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)
  if (!person.sicht) return null

  return (
    <div
      role="status"
      className="fixed inset-x-0 bottom-0 z-100 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 border-t-2 border-[#7a3a0e] bg-warn px-4 py-2 text-[12.5px] text-white"
    >
      <Eye className="size-4 shrink-0 opacity-90" />
      <span>
        Du siehst Work als <strong>{person.displayName}</strong> – nur ansehen, geändert wird
        nichts. Angemeldet bist du als {person.sicht.echterName}.
      </span>
      <button
        type="button"
        disabled={laeuft}
        onClick={() => {
          setLaeuft(true)
          setFehler(null)
          trpc.auth.sichtBeenden
            .mutate()
            .then(() => window.location.assign('/'))
            .catch((f: unknown) => {
              setLaeuft(false)
              setFehler(f instanceof Error ? f.message : 'Das hat nicht geklappt.')
            })
        }}
        className="cursor-pointer rounded-lg bg-white px-2.5 py-1 font-semibold text-warn disabled:opacity-60"
      >
        {laeuft ? 'Beendet …' : 'Sicht beenden'}
      </button>
      {fehler && <span className="font-semibold">{fehler}</span>}
    </div>
  )
}
